import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useQuery } from '../lib/useQuery.js';
import { Page } from '../app/AppShell.js';
import { useToast } from '../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../components/ui.js';
import { absoluteTime, relativeTime, rupiah } from '../lib/format.js';
import type { LicensedPlayer, LicenseOrder, LicenseOrderStatus, PlayerLicenseState } from '../lib/types.js';

/** How long to keep asking about an order after Midtrans sends the buyer back. */
const ORDER_POLL_MS = 3000;
const ORDER_POLL_TRIES = 40;

/**
 * The customer's portable players and their licence state, with "Buy licence"
 * for the ones that need one. Payment happens on Midtrans' hosted page; the
 * keys are minted when it settles and the portable CMS app hands each player
 * its key on its next sync, so nobody types a key anywhere.
 */
export function LicensesScreen(): ReactNode {
  const { can } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const overview = useQuery(() => api.licenseOverview(), []);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(params.getAll('select')));
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const returningOrderId = params.get('order');
  const returning = useOrderPoll(returningOrderId, () => overview.refetch());

  const canBuy = can('licenses.purchase') && overview.data?.sales.available === true;
  const players = overview.data?.data ?? [];
  const buyable = (p: LicensedPlayer): boolean => p.licenseId !== null && p.state !== 'licensed';
  const chosen = players.filter((p) => buyable(p) && p.licenseId !== null && selected.has(p.licenseId));
  const unitPrice = overview.data?.sales.unitPrice ?? 0;
  const free = overview.data?.sales.free === true;

  function toggle(licenseId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(licenseId)) next.delete(licenseId);
      else next.add(licenseId);
      return next;
    });
  }

  function selectAllUnlicensed(): void {
    setSelected(new Set(players.filter(buyable).map((p) => p.licenseId ?? '')));
  }

  async function buy(): Promise<void> {
    setBusy(true);
    try {
      const order = await api.buyLicenses(chosen.map((p) => p.licenseId ?? ''));
      // A free order comes back already issued.
      if (order.status === 'paid') {
        toast.push(
          `${order.quantity} licence${order.quantity === 1 ? '' : 's'} issued. Players activate on their next sync with your portable CMS app.`,
          'success',
        );
        setSelected(new Set());
        setBusy(false);
        overview.refetch();
        return;
      }
      if (!order.redirectUrl) throw new Error('Midtrans did not return a payment page.');
      window.location.assign(order.redirectUrl);
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  async function cancelOrder(order: LicenseOrder): Promise<void> {
    if (!confirm(`Cancel order ${order.orderRef}? Its payment link stops working.`)) return;
    setCancelling(order.id);
    try {
      const result = await api.cancelLicenseOrder(order.id);
      // The payment can land just before the cancel; then the order comes back paid.
      if (result.status === 'paid') toast.push('That order was already paid — its licences are issued.', 'success');
      else toast.push('Order cancelled.', 'success');
      overview.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setCancelling(null);
    }
  }

  const unlicensedCount = players.filter(buyable).length;

  return (
    <Page
      title="Licences"
      actions={
        canBuy && unlicensedCount > 0 ? (
          <button className="btn" onClick={selectAllUnlicensed}>
            Select all unlicensed ({unlicensedCount})
          </button>
        ) : null
      }
    >
      <p className="muted small" style={{ marginBottom: 12 }}>
        {free ? (
          <>
            Portable players run a 7-day trial, then need a licence. Licences are free: pick the players
            and get their licences at once. Each licence is permanent and tied to one player. Your portable
            CMS app delivers it the next time it syncs with the cloud, and the player unlocks by itself.
          </>
        ) : (
          <>
            Portable players run a 7-day trial, then need a licence. Pick the players to license and pay
            with QRIS, bank transfer, e-wallet or card. Each licence is permanent and tied to one player.
            Once paid, your portable CMS app delivers it the next time it syncs with the cloud, and the
            player unlocks by itself.
          </>
        )}
      </p>

      {returning ? (
        <OrderBanner
          order={returning.order}
          gaveUp={returning.gaveUp}
          onDismiss={() => {
            params.delete('order');
            setParams(params, { replace: true });
          }}
        />
      ) : null}

      {overview.loading && !overview.data ? <Loading what="players" /> : null}
      {overview.error ? <ErrorNote error={overview.error} /> : null}

      {overview.data ? (
        players.length === 0 ? (
          <EmptyState>
            No portable players yet. Sign the portable CMS app in to this account and its paired players
            appear here.
          </EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  {canBuy ? <th style={{ width: 32 }} /> : null}
                  <th>Player</th>
                  <th>Licence ID</th>
                  <th>Status</th>
                  <th>Reported</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.displayId}>
                    {canBuy ? (
                      <td>
                        {buyable(p) && p.licenseId !== null ? (
                          <input
                            type="checkbox"
                            aria-label={`Select ${p.name}`}
                            checked={selected.has(p.licenseId)}
                            onChange={() => toggle(p.licenseId ?? '')}
                          />
                        ) : null}
                      </td>
                    ) : null}
                    <td>{p.name}</td>
                    <td className="mono small">{p.licenseId ?? <span className="faint">not reported yet</span>}</td>
                    <td>
                      <LicenseStateBadge state={p.state} />
                    </td>
                    <td className="muted small" title={absoluteTime(p.reportedAt)}>
                      {relativeTime(p.reportedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {canBuy && chosen.length > 0 ? (
        <div className="card card-pad row between wrap" style={{ position: 'sticky', bottom: 16, marginTop: 16 }}>
          {free ? (
            <span>
              {chosen.length} player{chosen.length === 1 ? '' : 's'} · <strong>Free</strong>
            </span>
          ) : (
            <span>
              {chosen.length} player{chosen.length === 1 ? '' : 's'} × {rupiah(unitPrice)} ={' '}
              <strong>{rupiah(unitPrice * chosen.length)}</strong>
            </span>
          )}
          <button className="btn primary" disabled={busy} onClick={() => void buy()}>
            {free
              ? busy
                ? 'Issuing…'
                : `Get free licence${chosen.length === 1 ? '' : 's'}`
              : busy
                ? 'Opening payment…'
                : `Buy licence${chosen.length === 1 ? '' : 's'}`}
          </button>
        </div>
      ) : null}

      {overview.data && overview.data.orders.length > 0 && can('licenses.purchase') ? (
        <>
          <h3 style={{ margin: '24px 0 8px', fontSize: 14 }}>Orders</h3>
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Players</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {overview.data.orders.map((o) => (
                  <tr key={o.id}>
                    <td className="mono small">{o.orderRef}</td>
                    <td className="small">{o.quantity}</td>
                    <td className="small">{o.provider === 'free' ? 'Free' : rupiah(o.grossAmount)}</td>
                    <td>
                      <OrderStatusBadge status={o.status} />
                    </td>
                    <td className="muted small">{absoluteTime(o.paidAt ?? o.createdAt)}</td>
                    <td>
                      {o.status === 'pending' ? (
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          {o.redirectUrl ? (
                            <a className="btn small" href={o.redirectUrl}>
                              Continue payment
                            </a>
                          ) : null}
                          <button
                            className="btn small ghost"
                            disabled={cancelling === o.id}
                            onClick={() => void cancelOrder(o)}
                          >
                            {cancelling === o.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Page>
  );
}

/**
 * Follows the order Midtrans just sent the buyer back with (`?order=`) until
 * it settles. Each poll also asks Midtrans directly (server side), so this
 * completes the purchase even if the payment webhook never arrives.
 */
function useOrderPoll(
  orderId: string | null,
  onSettled: () => void,
): { order: LicenseOrder | null; gaveUp: boolean } | null {
  const [order, setOrder] = useState<LicenseOrder | null>(null);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    setOrder(null);
    setGaveUp(false);

    const poll = async (): Promise<void> => {
      tries += 1;
      try {
        const next = await api.licenseOrder(orderId);
        if (cancelled) return;
        setOrder(next);
        if (next.status !== 'pending') {
          onSettled();
          return;
        }
      } catch {
        // A transient failure is just another try.
      }
      if (cancelled) return;
      if (tries >= ORDER_POLL_TRIES) {
        setGaveUp(true);
        return;
      }
      timer = window.setTimeout(() => void poll(), ORDER_POLL_MS);
    };
    void poll();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Keyed on the order only: onSettled is a refetch callback, and re-polling
    // whenever its identity changed would restart the poll on every render.
  }, [orderId]);

  return orderId ? { order, gaveUp } : null;
}

function OrderBanner({
  order,
  gaveUp,
  onDismiss,
}: {
  order: LicenseOrder | null;
  gaveUp: boolean;
  onDismiss: () => void;
}): ReactNode {
  let tone = 'flat';
  let text = 'Checking your payment…';

  if (order?.status === 'paid') {
    tone = 'good';
    text = `Payment received — ${order.quantity} licence${order.quantity === 1 ? '' : 's'} issued. Players activate on their next sync with your portable CMS app.`;
  } else if (order?.status === 'failed') {
    tone = 'bad';
    text = 'The payment was declined or cancelled. No licence was issued.';
  } else if (order?.status === 'cancelled') {
    tone = 'flat';
    text = 'This order was cancelled. No licence was issued.';
  } else if (order?.status === 'expired') {
    tone = 'bad';
    text = 'The payment window expired. No licence was issued.';
  } else if (gaveUp) {
    tone = 'warn';
    text =
      'We haven’t heard from the payment provider yet. If you completed the payment, the licences appear here as soon as it is confirmed.';
  }

  return (
    <div className="card card-pad row between wrap" style={{ marginBottom: 16 }}>
      <span className={`badge ${tone}`} style={{ whiteSpace: 'normal' }}>
        {text}
      </span>
      <button className="btn small ghost" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

export function LicenseStateBadge({ state }: { state: PlayerLicenseState }): ReactNode {
  const [tone, label] = {
    licensed: ['good', 'Licensed'],
    trial: ['warn', 'Trial'],
    expired: ['bad', 'Unlicensed'],
    unknown: ['flat', 'Unknown'],
  }[state];
  return <span className={`badge ${tone}`}>{label}</span>;
}

function OrderStatusBadge({ status }: { status: LicenseOrderStatus }): ReactNode {
  const [tone, label] = {
    pending: ['warn', 'Awaiting payment'],
    paid: ['good', 'Paid'],
    failed: ['bad', 'Failed'],
    expired: ['flat', 'Expired'],
    cancelled: ['flat', 'Cancelled'],
  }[status];
  return <span className={`badge ${tone}`}>{label}</span>;
}
